import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Pencil, Phone, Trash2 } from 'lucide-react'
import { useState } from 'react'

interface RoutingTargetCardProps {
    index: number
    routingTargetObject: {
        phoneNumber?: string
        targetDescription?: string
        utteranceLanguage?: string
    }
    callRoutingTargetNumber: string
    onUpdate: (data: any) => void
    onDelete: () => void
}

export function RoutingTargetCard({
    index,
    routingTargetObject,
    callRoutingTargetNumber,
    onUpdate,
    onDelete
}: RoutingTargetCardProps) {
    const [editMode, setEditMode] = useState(false)
    const [editingPhoneNumber, setEditingPhoneNumber] = useState(routingTargetObject.phoneNumber || '')
    const [editingDescription, setEditingDescription] = useState(routingTargetObject.targetDescription || '')
    const [editingLanguage, setEditingLanguage] = useState(routingTargetObject.utteranceLanguage || 'de')
    const [editingTargetNumber, setEditingTargetNumber] = useState(callRoutingTargetNumber)

    const toggleEditMode = () => {
        if (editMode) {
            // Reset to original values when cancelling
            setEditingPhoneNumber(routingTargetObject.phoneNumber || '')
            setEditingDescription(routingTargetObject.targetDescription || '')
            setEditingLanguage(routingTargetObject.utteranceLanguage || 'de')
            setEditingTargetNumber(callRoutingTargetNumber)
        }
        setEditMode(!editMode)
    }

    const handleSave = () => {
        onUpdate({
            index,
            targetNumber: editingTargetNumber,
            originalTargetNumber: callRoutingTargetNumber,
            phoneNumber: editingPhoneNumber,
            targetDescription: editingDescription,
            utteranceLanguage: editingLanguage
        })
        setEditMode(false)
    }

    if (editMode) {
        return (
            <Card className="border border-gray-200">
                <CardContent className="pt-4 space-y-4">
                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Zielnummer</Label>
                        <Input
                            value={editingTargetNumber}
                            onChange={(e) => setEditingTargetNumber(e.target.value)}
                            className="h-10"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Telefonnummer</Label>
                        <Input
                            value={editingPhoneNumber}
                            onChange={(e) => setEditingPhoneNumber(e.target.value)}
                            placeholder="+49..."
                            className="h-10"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Beschreibung</Label>
                        <Input
                            value={editingDescription}
                            onChange={(e) => setEditingDescription(e.target.value)}
                            placeholder="Beschreibung..."
                            className="h-10"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label className="text-sm text-gray-600">Sprache</Label>
                        <Select value={editingLanguage} onValueChange={setEditingLanguage}>
                            <SelectTrigger className="h-10">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="de">DE</SelectItem>
                                <SelectItem value="en">EN</SelectItem>
                                <SelectItem value="fr">FR</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSave}
                            className="border-brand-primary text-brand-primary hover:bg-brand-primary/10"
                        >
                            Speichern
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleEditMode}
                            className="text-gray-500"
                        >
                            Abbrechen
                        </Button>
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className="border border-gray-200 hover:shadow-sm transition-shadow">
            <CardContent className="p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary font-semibold">
                            {callRoutingTargetNumber}
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 text-sm text-gray-900 font-medium">
                                <Phone className="h-4 w-4 text-brand-primary" />
                                {routingTargetObject.phoneNumber || '-'}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                                {routingTargetObject.targetDescription || 'Keine Beschreibung'}
                            </p>
                        </div>
                        <div className="flex items-center justify-center w-8 h-8 rounded bg-gray-100">
                            <span className="text-xs font-medium text-gray-700">
                                {(routingTargetObject.utteranceLanguage || 'de').toUpperCase()}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={toggleEditMode}
                            className="h-8 w-8 p-0"
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onDelete}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}
